import type { Feature, FeatureCollection, LineString } from 'geojson'
import type { IAreaBoundary } from '@/entities/destination'

/** Internal convention: [lat, lng]. GeoJSON/API uses [lng, lat]. */
export type MapCoordinate = [number, number]

export interface MapLineGeoJson extends Feature<LineString> {}

function isLatitude(x: number): boolean {
  return x >= -90 && x <= 90
}

function isLongitude(x: number): boolean {
  return x >= -180 && x <= 180
}

function pairToLatLng(a: number, b: number): MapCoordinate | null {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null
  if (!isLongitude(a) || !isLongitude(b)) return null
  const aIsLat = isLatitude(a)
  const bIsLat = isLatitude(b)
  if (aIsLat && !bIsLat) return [a, b]
  if (!aIsLat && bIsLat) return [b, a]
  if (aIsLat && bIsLat) return [a, b]
  return null
}

export function isFiniteCoordinatePair(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    typeof value[0] === 'number' &&
    Number.isFinite(value[0]) &&
    typeof value[1] === 'number' &&
    Number.isFinite(value[1])
  )
}

export function normalizeCoordinates(coordinates: unknown): MapCoordinate[] {
  if (!Array.isArray(coordinates)) return []
  const result: MapCoordinate[] = []
  for (const item of coordinates) {
    if (!Array.isArray(item) || item.length < 2) continue
    const pair = pairToLatLng(Number(item[0]), Number(item[1]))
    if (pair) result.push(pair)
  }
  return result
}

export function toMapboxLineString(coordinates: unknown): MapLineGeoJson | null {
  const normalized = normalizeCoordinates(coordinates)
  if (normalized.length < 2) return null
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: normalized.map(([lat, lng]) => [lng, lat]),
    },
  }
}

export function fromMapboxLineString(feature: Feature<LineString>): MapCoordinate[] {
  return feature.geometry.coordinates
    .filter(
      (point) =>
        Array.isArray(point) &&
        point.length >= 2 &&
        Number.isFinite(Number(point[0])) &&
        Number.isFinite(Number(point[1])),
    )
    .map((point) => [Number(point[1]), Number(point[0])])
}

export function toLineFeatureCollection(
  coordinates: MapCoordinate[],
): FeatureCollection<LineString> | null {
  const line = toMapboxLineString(coordinates)
  if (!line) return null
  return { type: 'FeatureCollection', features: [line] }
}

export function createBoundaryGeoJson(boundary?: IAreaBoundary | null) {
  if (!boundary) return null
  const rawCoordinates = Array.isArray(boundary)
    ? boundary
    : boundary.type === 'Polygon' && Array.isArray(boundary.coordinates)
      ? boundary.coordinates
      : null
  if (!rawCoordinates) return null
  const coordinates = rawCoordinates
    .map((ring) => (Array.isArray(ring) ? ring.filter(isFiniteCoordinatePair) : []))
    .filter((ring) => ring.length >= 4)
  if (coordinates.length < 1) return null
  return {
    type: 'FeatureCollection' as const,
    features: [
      {
        type: 'Feature' as const,
        properties: {},
        geometry: { type: 'Polygon' as const, coordinates },
      },
    ],
  }
}

export function getBoundaryBounds(
  boundary?: IAreaBoundary | null,
): [[number, number], [number, number]] | null {
  const geoJson = createBoundaryGeoJson(boundary)
  const points = geoJson?.features[0]?.geometry.coordinates.flat() ?? []
  if (points.length < 1) return null
  const longitudes = points.map((point) => point[0])
  const latitudes = points.map((point) => point[1])
  return [
    [Math.min(...longitudes), Math.min(...latitudes)],
    [Math.max(...longitudes), Math.max(...latitudes)],
  ]
}

export function mergeBounds(
  bounds: ([[number, number], [number, number]] | null)[],
): [[number, number], [number, number]] | null {
  const valid = bounds.filter((b): b is [[number, number], [number, number]] => Boolean(b))
  if (valid.length < 1) return null
  return [
    [Math.min(...valid.map((b) => b[0][0])), Math.min(...valid.map((b) => b[0][1]))],
    [Math.max(...valid.map((b) => b[1][0])), Math.max(...valid.map((b) => b[1][1]))],
  ]
}

export function appendCoordinate(coords: MapCoordinate[], point: MapCoordinate): MapCoordinate[] {
  return [...coords, point]
}

export function updateCoordinateAt(
  coords: MapCoordinate[],
  index: number,
  point: MapCoordinate,
): MapCoordinate[] {
  const next = coords.slice()
  next[index] = point
  return next
}

export function removeCoordinateAt(coords: MapCoordinate[], index: number): MapCoordinate[] {
  return coords.filter((_, i) => i !== index)
}

export function insertCoordinateAt(
  coords: MapCoordinate[],
  index: number,
  point: MapCoordinate,
): MapCoordinate[] {
  const next = coords.slice()
  next.splice(index, 0, point)
  return next
}

export function midpoint(a: MapCoordinate, b: MapCoordinate): MapCoordinate {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
}

export function validateCoordinatePoint(point: [unknown, unknown]): point is [number, number] {
  if (!point) return false
  const [lat, lng] = point
  return (
    typeof lat === 'number' &&
    Number.isFinite(lat) &&
    lat >= -90 &&
    lat <= 90 &&
    typeof lng === 'number' &&
    Number.isFinite(lng) &&
    lng >= -180 &&
    lng <= 180
  )
}

/**
 * Convert a GeoJSON polygon ring [lng, lat][] → internal MapCoordinate [lat, lng][].
 * Strips the closing duplicate point if present.
 */
export function geoJsonRingToMapCoordinates(ring: number[][]): MapCoordinate[] {
  const coords = ring
    .filter(isFiniteCoordinatePair)
    .map(([lng, lat]) => [lat, lng] as MapCoordinate)
  const first = coords[0]
  const last = coords[coords.length - 1]
  if (first && last && first[0] === last[0] && first[1] === last[1] && coords.length > 1) {
    return coords.slice(0, -1)
  }
  return coords
}

/**
 * Convert internal MapCoordinate [lat, lng][] → GeoJSON ring [lng, lat][].
 * Auto-closes the ring (appends first point if not already closed).
 */
export function mapCoordinatesToGeoJsonRing(coords: MapCoordinate[]): number[][] {
  if (coords.length < 3) return []
  const ring = coords.map(([lat, lng]) => [lng, lat])
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    ring.push([...first])
  }
  return ring
}

/** Build IAreaBoundary from internal polygon coordinates. Returns null if < 3 points. */
export function mapCoordinatesToAreaBoundary(coords: MapCoordinate[]): IAreaBoundary | null {
  const ring = mapCoordinatesToGeoJsonRing(coords)
  if (ring.length < 4) return null
  return { type: 'Polygon', coordinates: [ring] }
}

/** Extract first ring from IAreaBoundary as internal MapCoordinate[] (no closing point). */
export function areaBoundaryToMapCoordinates(boundary: IAreaBoundary | null): MapCoordinate[] {
  if (!boundary) return []
  const ring = boundary.coordinates?.[0]
  if (!ring) return []
  return geoJsonRingToMapCoordinates(ring)
}
